import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import BackToTop from "@/components/feature/BackToTop";
import Breadcrumb from "@/components/feature/Breadcrumb";
import StatusBadge from "@/components/base/StatusBadge";
import { useSeo } from "@/hooks/useSeo";
import { mythItems } from "@/mocks/faq";

const SEO_TITLE = "辟谣专区 - 网报吃瓜已澄清传言与官方辟谣回应汇总";
const SEO_DESC =
  "网报吃瓜辟谣专区汇总已被官方澄清的传言，逐条对照传言原文与官方回应，标注发布主体与澄清日期，转发前先查一查。";
const SEO_KEYWORDS = "网报吃瓜辟谣,网络传言澄清,官方辟谣回应,谣言核查,网报吃瓜官方声明";

const filterTabs = ["全部", "已辟谣", "已通报", "已结案", "调查中"];

const detectTips = [
  {
    icon: "ri-user-voice-line",
    title: "没有落款的截图",
    desc: "只有聊天记录截图、没有发布主体的转载，通常不是官方原文。",
  },
  {
    icon: "ri-calendar-close-line",
    title: "没有时间与出处",
    desc: "旧闻新发很常见，配图与小票都不标日期时，先核对发布时间。",
  },
  {
    icon: "ri-emotion-unhappy-line",
    title: "情绪词特别多",
    desc: "「全城」「一定」「务必」等绝对化表达，往往是传播放大后的说法。",
  },
  {
    icon: "ri-links-line",
    title: "查不到原始链接",
    desc: "正规通报一般能在官方网站或认证账号找到原文与时间。",
  },
];

export default function Mythbusting() {
  const [tab, setTab] = useState("全部");

  const filtered = useMemo(
    () => (tab === "全部" ? mythItems : mythItems.filter((item) => item.status === tab)),
    [tab],
  );

  useSeo({
    title: SEO_TITLE,
    description: SEO_DESC,
    keywords: SEO_KEYWORDS,
    path: "/mythbusting",
    jsonLd: {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "CollectionPage",
          name: SEO_TITLE,
          description: SEO_DESC,
          url: "https://sxdayun.com.cn/mythbusting",
        },
        {
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "首页", item: "https://sxdayun.com.cn/" },
            {
              "@type": "ListItem",
              position: 2,
              name: "辟谣专区",
              item: "https://sxdayun.com.cn/mythbusting",
            },
          ],
        },
      ],
    },
  });

  return (
    <div className="flex min-h-screen w-full flex-col bg-background-50">
      <SiteHeader />
      <main className="w-full">
        <section className="w-full border-b border-background-200 bg-background-100/60">
          <div className="mx-auto w-full max-w-[1200px] px-4 py-8 md:px-8 md:py-12">
            <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "辟谣专区" }]} />
            <h1 className="mt-4 font-heading text-2xl font-bold text-foreground-950 md:text-4xl">
              辟谣专区
            </h1>
            <p className="mt-3 max-w-3xl text-sm leading-relaxed text-foreground-600 md:text-base">
              这里把正在传播的说法与官方回应逐条对照放置。左边是流传的说法，右边是官方澄清的表述，转发之前建议先看一眼。
            </p>
          </div>
        </section>

        <section className="w-full">
          <div className="mx-auto w-full max-w-[1200px] px-4 py-10 md:px-8 md:py-14">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {detectTips.map((tip) => (
                <div
                  key={tip.title}
                  className="rounded-lg border border-background-200 bg-background-100/60 p-5"
                >
                  <span className="flex h-10 w-10 items-center justify-center rounded-md bg-accent-100 text-accent-700">
                    <i className={`${tip.icon} text-xl`}></i>
                  </span>
                  <h2 className="mt-3 font-heading text-sm font-bold text-foreground-950">
                    {tip.title}
                  </h2>
                  <p className="mt-1.5 text-xs leading-relaxed text-foreground-600">{tip.desc}</p>
                </div>
              ))}
            </div>

            <div className="mt-10 flex flex-wrap items-center gap-2 border-t border-background-200 pt-8">
              <span className="mr-1 text-xs font-medium text-foreground-500">状态</span>
              {filterTabs.map((item) => (
                <button
                  key={item}
                  type="button"
                  onClick={() => setTab(item)}
                  className={`rounded-full border px-3.5 py-1.5 text-xs transition-colors ${
                    tab === item
                      ? "border-accent-500 bg-accent-500 text-background-50"
                      : "border-background-300 bg-background-50 text-foreground-700 hover:border-accent-300"
                  }`}
                >
                  {item}
                </button>
              ))}
            </div>

            <div className="mt-6 flex flex-col gap-5">
              {filtered.map((item) => (
                <article
                  key={item.id}
                  className="overflow-hidden rounded-lg border border-background-200 bg-background-50"
                >
                  <div className="grid grid-cols-1 md:grid-cols-2">
                    <div className="border-b border-background-200 bg-background-100/60 p-5 md:border-b-0 md:border-r md:p-6">
                      <div className="flex items-center gap-2 text-xs font-medium text-foreground-500">
                        <span className="flex h-4 w-4 items-center justify-center text-accent-600">
                          <i className="ri-error-warning-line"></i>
                        </span>
                        流传说法
                      </div>
                      <p className="mt-3 font-heading text-base font-semibold leading-relaxed text-foreground-700">
                        {item.rumor}
                      </p>
                    </div>
                    <div className="p-5 md:p-6">
                      <div className="flex items-center gap-2 text-xs font-medium text-secondary-700">
                        <span className="flex h-4 w-4 items-center justify-center">
                          <i className="ri-shield-check-line"></i>
                        </span>
                        官方澄清
                      </div>
                      <p className="mt-3 text-sm leading-relaxed text-foreground-800">
                        {item.truth}
                      </p>
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-x-5 gap-y-2 border-t border-background-200 px-5 py-4 md:px-6">
                    <StatusBadge status={item.status} />
                    <span className="inline-flex items-center gap-1.5 text-xs text-foreground-500">
                      <span className="flex h-3.5 w-3.5 items-center justify-center">
                        <i className="ri-verified-badge-line"></i>
                      </span>
                      {item.actor}
                    </span>
                    <span className="inline-flex items-center gap-1.5 text-xs text-foreground-500">
                      <span className="flex h-3.5 w-3.5 items-center justify-center">
                        <i className="ri-calendar-line"></i>
                      </span>
                      {item.date}
                    </span>
                    <Link
                      to={`/events/${item.eventId}`}
                      className="ml-auto inline-flex items-center gap-1.5 whitespace-nowrap text-xs font-medium text-primary-700 hover:text-primary-600"
                    >
                      查看完整时间线
                      <span className="flex h-3.5 w-3.5 items-center justify-center">
                        <i className="ri-arrow-right-line"></i>
                      </span>
                    </Link>
                  </div>
                </article>
              ))}
            </div>

            {filtered.length === 0 && (
              <div className="mt-6 flex flex-col items-center rounded-lg border border-dashed border-background-300 bg-background-100/60 px-6 py-16 text-center">
                <span className="flex h-12 w-12 items-center justify-center rounded-full bg-background-200 text-foreground-500">
                  <i className="ri-shield-check-line text-2xl"></i>
                </span>
                <h2 className="mt-4 font-heading text-lg font-bold text-foreground-950">
                  该状态下暂无记录
                </h2>
                <p className="mt-2 max-w-md text-sm leading-relaxed text-foreground-600">
                  切换到其他状态，或前往事件追踪列表查看全部收录条目。
                </p>
                <button
                  type="button"
                  onClick={() => setTab("全部")}
                  className="mt-5 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 hover:bg-primary-600"
                >
                  <span className="flex h-4 w-4 items-center justify-center">
                    <i className="ri-refresh-line"></i>
                  </span>
                  查看全部
                </button>
              </div>
            )}

            <div className="mt-10 rounded-lg border border-background-200 bg-secondary-50 p-6 md:p-8">
              <div className="flex flex-col items-start justify-between gap-4 md:flex-row md:items-center">
                <div>
                  <h2 className="font-heading text-lg font-bold text-foreground-950">
                    群里收到了没收录的传言？
                  </h2>
                  <p className="mt-2 max-w-2xl text-sm leading-relaxed text-foreground-600">
                    把传言原文与你能找到的官方链接一起发来，我们会核对后补充到辟谣专区与对应时间线。
                  </p>
                </div>
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-secondary-600 px-5 py-2.5 text-sm font-medium text-background-50 hover:bg-secondary-700"
                >
                  <span className="flex h-4 w-4 items-center justify-center">
                    <i className="ri-send-plane-line"></i>
                  </span>
                  提交待核实传言
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
      <BackToTop />
    </div>
  );
}